import { Link } from "react-router-dom";
import { useJourneyState } from "@/lib/useJourneyState";

/**
 * Lifecycle-aware panel for the dashboard. Renders only when the state
 * deserves attention — for steady "active" users the surface is silent.
 *
 * Intentionally typographic, intentionally restrained:
 *   - No progress bar, no checklist, no percentage
 *   - At most 3 suggestions, each a single quiet line
 *   - Label + explanation set the tone; suggestions are subordinate
 *   - Never says "you missed", "level up", "complete", or "unlock"
 */
export function JourneyPanel() {
  const { data, isLoading } = useJourneyState();
  if (isLoading || !data) return null;
  if (data.state === "active") return null;
  if (data.suggestions.length === 0 && !data.explanation) return null;

  return (
    <section className="mt-12 first:mt-0">
      <header className="mb-4">
        <p className="label-eyebrow">{data.label}</p>
        {data.explanation && (
          <h2 className="mt-1.5 font-display text-lg tracking-tight max-w-2xl leading-snug">
            {data.explanation}
          </h2>
        )}
      </header>
      {data.suggestions.length > 0 && (
        <ul className="panel divide-y divide-veil/30">
          {data.suggestions.map((s, i) => (
            <li key={i}>
              {s.link ? (
                <Link
                  to={s.link}
                  className="block px-6 py-4 hover:bg-veil/15 transition-colors group"
                >
                  <SuggestionRow title={s.title} />
                </Link>
              ) : (
                <div className="px-6 py-4">
                  <SuggestionRow title={s.title} muted />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SuggestionRow({ title, muted = false }: { title: string; muted?: boolean }) {
  return (
    <p
      className={
        muted
          ? "text-sm text-whisper leading-relaxed"
          : "text-sm text-fog leading-relaxed group-hover:text-gold-glow transition-colors"
      }
    >
      {title}
    </p>
  );
}
